import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { pageViewTrackingPlugin } from '@amplitude/plugin-page-view-tracking-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';

import { getWeekStartDate, getMonthStartDate } from '@arthelper/shared/date';
import { isSetOnceAttributionKey } from '@arthelper/shared/attribution';
import { flushOnExit } from '@arthelper/shared/amplitude-exit';

import { changePageTitlesPlugin, PAGE_VIEWED_EVENT_NAME } from './custom-plugins';
import { Events } from './events';
import { AmplitudeIdentifyParams, AmplitudeInterface } from './types';

const init = (apiKey: string) => {
  const sessionReplayTracking = sessionReplayPlugin();
  const pageViewTracking = pageViewTrackingPlugin({ eventType: PAGE_VIEWED_EVENT_NAME });
  const engagement = engagementPlugin();

  amplitude.add(engagement);

  amplitude.add(sessionReplayTracking);
  amplitude.add(pageViewTracking);

  amplitude.add(changePageTitlesPlugin);

  amplitude.init(apiKey, {
    autocapture: {
      // pageViewTracking above is the one page-view source; the SDK skips its same-named autocapture copy anyway
      pageViews: false,
      elementInteractions: false,
      formInteractions: false,
      attribution: {
        excludeReferrers: [
          'arthelper.ai',
          /.*\.arthelper\.ai/,
          'arthelper.com',
          /.*\.arthelper\.com/,
          'tryarthelper.com',
          /.*\.tryarthelper\.com/,
        ],
      },
    },
    cookieOptions: { domain: '.arthelper.ai' },
    minIdLength: 1,
  });
};

const track = <
  EventName extends keyof Events,
  K extends Events[EventName],
  Properties extends K extends { properties: unknown } ? [K['properties']] : [undefined?],
>(
  eventName: EventName,
  ...rest: Properties
) => {
  const properties = rest[0];

  amplitude.track(eventName, properties);
};

const identify = ({
  id,
  email,
  username,
  firstName,
  lastName,
  createdAt,
  isOldAsfMember,
  isCoreFunnelLead,
  hasSettingsInstructions,
  settingsInstructions,
  attribution,
}: AmplitudeIdentifyParams) => {
  const identifyEvent = new amplitude.Identify();

  identifyEvent.set('user_id', id);
  identifyEvent.set('email', email);

  if (username) identifyEvent.set('username', username);
  if (firstName) identifyEvent.set('first_name', firstName);
  if (lastName) identifyEvent.set('last_name', lastName);

  const fullname = `${firstName} ${lastName}`.trim() || null;
  if (fullname) identifyEvent.set('full_name', fullname);

  identifyEvent.set('account_created_date', createdAt);

  const createdAtDate = new Date(createdAt);
  identifyEvent.set('account_created_date_week', getWeekStartDate(createdAtDate));
  identifyEvent.set('account_created_date_month', getMonthStartDate(createdAtDate));

  identifyEvent.set('is_old_asf_member', isOldAsfMember);
  identifyEvent.set('is_core_funnel_lead', isCoreFunnelLead);
  identifyEvent.set('has_settings_instructions', hasSettingsInstructions);
  identifyEvent.set('settings_instructions', settingsInstructions);

  // Marketing attribution: last-touch via `set`, set-once keys (first-touch and
  // the referring-user id) via `setOnce` so a later visit can never overwrite
  // them. Keys are open-ended (any utm_* + recognized click IDs); we iterate the
  // payload directly and route by `isSetOnceAttributionKey`. Self-referral is
  // filtered upstream by `getReferralProperty`, so whatever arrives here is
  // already emittable.
  if (attribution) {
    for (const [key, value] of Object.entries(attribution)) {
      if (!value) continue;
      if (isSetOnceAttributionKey(key)) {
        identifyEvent.setOnce(key, value);
      } else {
        identifyEvent.set(key, value);
      }
    }
  }

  amplitude.setUserId(id);
  amplitude.identify(identifyEvent);
};

/**
 * @description
 * need to add delay to avoid events that are execuded right before 'reset' being transmitted without user_id
 * and user is marked as Annonymous  */
const reset = () => {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      amplitude.reset();
      resolve();
    }, 500);
  });
};

export const initAmplitude = (apiKey: string): AmplitudeInterface => {
  init(apiKey);

  return {
    track,
    // beacon drain for same-tab navigation — see `@arthelper/shared/amplitude-exit`
    flushOnExit: () => {
      flushOnExit(amplitude);
    },
    identify,
    reset,
  };
};
